/**
 * Detection / Incidents (DDR response loop, delivery queue Q42).
 *
 * One screen to drive a detection to a closed, evidenced outcome. Decisions
 * are grouped into incidents (same actor and store within an hour) and ranked
 * by severity, which includes what the store holds (DSPM labels). From here an
 * analyst assigns, acknowledges, suppresses, runs a playbook, approves one
 * (a different person), starts break-glass, confirms a breach and closes with
 * an outcome. Every action shows who did it and when; every refusal says why.
 *
 * Live data:
 *   GET  /v1/detection/incidents, /v1/detection/incidents/:id
 *   POST /v1/detection/incidents/:id/{assign,acknowledge,suppress,playbooks,break-glass,confirm-breach,close}
 *   POST /v1/detection/playbook-runs/:id/approve, /steps/:index/complete
 *   POST /v1/detection/breaches/:id/clocks/:key/done
 *
 * The persona specification has no Incidents row yet, so the page sits under
 * the Operator Dashboard's access decision (same permission and licence) and
 * is reached from there; it is not in the sidebar.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Flame, Loader2, ShieldAlert, ShieldCheck, User } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/primitives';
import { RouteGuard } from '@/components/shell/RouteGuard';
import { useCaller, useCan } from '@/lib/access';
import { listDetectionPlaybooks, type DetectionPlaybook } from '@/lib/api';
import {
  acknowledgeIncident,
  approvePlaybookRun,
  assignIncident,
  closeIncident,
  completeManualStep,
  confirmIncidentBreach,
  getIncident,
  listIncidents,
  markBreachClockDone,
  refusalText,
  runIncidentPlaybook,
  startIncidentBreakGlass,
  suppressIncident,
  type BreachClock,
  type Incident,
  type IncidentCounts,
  type IncidentDetail,
  type IncidentOutcome,
  type SuppressionScope,
} from '@/lib/detection-response-api';

type Filter = 'active' | 'closed' | 'all' | 'suppressed';

const SEVERITY_VARIANT = { critical: 'critical', high: 'high', medium: 'medium', low: 'low' } as const;

const SENSITIVITY_TEXT: Record<Incident['storeSensitivity'], string> = {
  sensitive: 'holds sensitive data (identity, financial, health or children\'s)',
  personal: 'holds personal data',
  unclassified: 'has nothing classified',
};

const STATUS_TEXT: Record<Incident['status'], string> = {
  open: 'Open',
  acknowledged: 'Acknowledged',
  contained: 'Contained',
  closed: 'Closed',
};

const ACTION_TEXT: Record<string, string> = {
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  acknowledged: 'Acknowledged',
  suppressed: 'Suppressed',
  suppression_ended: 'Suppression ended',
  playbook_requested: 'Playbook requested',
  playbook_run: 'Playbook run',
  playbook_approved: 'Playbook approved and run',
  manual_step_done: 'Manual step recorded',
  break_glass_requested: 'Break-glass requested',
  break_glass_approved: 'Break-glass approved',
  break_glass_summary_filed: 'Break-glass summary filed',
  breach_confirmed: 'Confirmed as breach',
  breach_clock_done: 'Regulator filing recorded',
  closed: 'Closed',
};

const OUTCOME_TEXT: Record<IncidentOutcome, string> = {
  true_positive: 'True positive',
  false_positive: 'False positive',
  benign: 'Benign',
};

/** suppression.ts isGuardSignature, as the console needs it. */
const GUARD_SIGNATURES: ReadonlySet<string> = new Set(
  ['section_9_guardrail', 'principal_enforcement_guard', 'honeytoken_guard', 'server_side_copy_guard', 'unreadable_statement_guard', 'column_attribution_guard', 'policy_graph'].map((e) => `engine:${e}`),
);

const SCOPE_TEXT: Record<SuppressionScope, string> = {
  actor_store: 'This actor on this store',
  actor: 'This actor on every store',
  signature: 'This rule or engine for everyone',
};

const DURATIONS: Array<{ hours: number; label: string }> = [
  { hours: 1, label: '1 hour' },
  { hours: 24, label: '1 day' },
  { hours: 24 * 7, label: '7 days' },
  { hours: 24 * 30, label: '30 days' },
  { hours: 24 * 90, label: '90 days' },
];

function when(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

function remaining(ms: number): string {
  const m = Math.round(Math.abs(ms) / 60_000);
  const text = m < 60 ? `${m} min` : m < 48 * 60 ? `${Math.round(m / 60)} h` : `${Math.round(m / 1440)} d`;
  return ms >= 0 ? `${text} left` : `${text} overdue`;
}

const CLOCK_CLASS: Record<BreachClock['state'], string> = {
  green: 'border-success/40 text-success',
  amber: 'border-warning/60 text-warning',
  red: 'border-sev-high/60 text-sev-high',
  overdue: 'border-sev-critical/70 text-sev-critical',
  done: 'border-muted text-muted-foreground',
};

export function ClockChips({ clocks }: { clocks: BreachClock[] }) {
  if (clocks.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="breach-clocks">
      {clocks.map((c) => (
        <span key={c.key} title={c.rule} data-clock={c.key} data-state={c.state} className={`text-[10px] border rounded px-1.5 py-0.5 ${CLOCK_CLASS[c.state]}`}>
          {c.label}: {c.state === 'done' ? `filed ${when(c.doneAt)}` : remaining(c.remainingMs)}
        </span>
      ))}
    </div>
  );
}

export function DetectionIncidents() {
  return (
    <RouteGuard pathname="/detection">
      <IncidentsScreen />
    </RouteGuard>
  );
}

function IncidentsScreen() {
  const access = useCaller();
  const me = access.status === 'ready' ? access.me : null;
  const canAck = useCan('detection:event:acknowledge');
  const canRun = useCan('detection:playbook:run');
  const canApprove = useCan('detection:rule:approve');

  const [filter, setFilter] = useState<Filter>('active');
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [counts, setCounts] = useState<IncidentCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cursor, setCursor] = useState(0);
  const [selected, setSelected] = useState<string | null>(() => new URLSearchParams(window.location.search).get('incident'));
  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'refused'; text: string } | null>(null);
  const [playbooks, setPlaybooks] = useState<DetectionPlaybook[]>([]);
  const [dialog, setDialog] = useState<null | 'suppress' | 'playbook' | 'breakglass' | 'breach' | 'close'>(null);
  const [offer, setOffer] = useState<{ scope: SuppressionScope; incidentId: string } | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await listIncidents(filter === 'suppressed' ? { status: 'all', suppressed: true } : { status: filter });
      setIncidents(r.incidents);
      setCounts(r.counts);
      setLoadError(null);
    } catch (err) {
      setLoadError(refusalText(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      setDetail(await getIncident(id));
    } catch (err) {
      setDetail(null);
      setNotice({ kind: 'refused', text: refusalText(err) });
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);
  useEffect(() => {
    if (selected) void loadDetail(selected);
  }, [selected, loadDetail]);
  useEffect(() => {
    listDetectionPlaybooks().then((r) => setPlaybooks(r.playbooks)).catch(() => setPlaybooks([]));
  }, []);

  async function act(label: string, fn: () => Promise<unknown>, ok: string) {
    setBusy(label);
    setNotice(null);
    try {
      await fn();
      setNotice({ kind: 'ok', text: ok });
      await load();
      if (selected) await loadDetail(selected);
      return true;
    } catch (err) {
      setNotice({ kind: 'refused', text: refusalText(err) });
      return false;
    } finally {
      setBusy(null);
    }
  }

  function onListKey(e: KeyboardEvent<HTMLUListElement>) {
    if (incidents.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, incidents.length - 1));
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const inc = incidents[cursor];
      if (inc) setSelected(inc.id);
    }
  }

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${cursor}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [cursor]);

  const inc = detail?.incident ?? null;
  const pendingRuns = useMemo(() => (detail?.playbookRuns ?? []).filter((r) => r.status === 'pending_approval'), [detail]);
  const closed = inc?.status === 'closed';

  return (
    <PageContainer>
      <PageHeader
        title="Incidents"
        subtitle="Detections grouped by actor and store, most serious first. Act on each one here until it is closed."
        badges={
          counts
            ? [
                { label: `${counts.open} open`, variant: counts.open > 0 ? 'high' : 'glass' },
                { label: `${counts.acknowledged + counts.contained} in progress`, variant: 'glass' },
                { label: `${counts.closed} closed`, variant: 'glass' },
              ]
            : []
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-3" role="tablist" aria-label="Which incidents">
        {(
          [
            ['active', 'Open and in progress'],
            ['closed', 'Closed'],
            ['all', 'All'],
            ['suppressed', `With suppressed hits${counts ? ` (${counts.withSuppressedHits})` : ''}`],
          ] as Array<[Filter, string]>
        ).map(([f, label]) => (
          <Button key={f} size="sm" role="tab" aria-selected={filter === f} variant={filter === f ? 'default' : 'glass'} onClick={() => { setFilter(f); setCursor(0); }}>
            {label}
          </Button>
        ))}
        <Link to="/detection" className="text-xs text-muted-foreground underline ml-auto">Operator dashboard</Link>
      </div>

      {notice && (
        <div role="status" data-testid="incident-notice" className={`glass-lite border rounded-md p-3 mb-3 text-xs flex items-start gap-2 ${notice.kind === 'refused' ? 'border-sev-high/40' : 'border-success/40'}`}>
          {notice.kind === 'refused' ? <AlertTriangle className="h-4 w-4 text-sev-high shrink-0" /> : <CheckCircle2 className="h-4 w-4 text-success shrink-0" />}
          <div className="flex-1">{notice.kind === 'refused' ? `Not done: ${notice.text}` : notice.text}</div>
          {offer && notice.kind === 'ok' && (
            <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => void act('offer', () => suppressIncident(offer.incidentId, { scope: offer.scope, reason: 'Closed as a false positive', expiresInHours: 24 * 30 }), `Suppressed for 30 days: ${SCOPE_TEXT[offer.scope].toLowerCase()}.`).then(() => setOffer(null))}>
              Suppress for 30 days: {SCOPE_TEXT[offer.scope].toLowerCase()}
            </Button>
          )}
          <Button size="sm" variant="glass" onClick={() => { setNotice(null); setOffer(null); }}>Dismiss</Button>
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <GlassCard className="p-0 overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading incidents...</div>
          ) : loadError ? (
            <div className="p-6 text-sm">
              <div className="font-medium">Incidents could not be loaded.</div>
              <div className="text-muted-foreground mt-1">{loadError}</div>
              <Button size="sm" variant="glass" className="mt-3" onClick={() => { setLoading(true); void load(); }}>Try again</Button>
            </div>
          ) : incidents.length === 0 ? (
            <div className="p-6 text-sm" data-testid="incidents-empty">
              <ShieldCheck className="h-6 w-6 mb-2 opacity-50" />
              <div className="font-medium">{filter === 'closed' ? 'No closed incidents yet.' : filter === 'suppressed' ? 'No incident has suppressed hits.' : 'No open incidents.'}</div>
              <div className="text-muted-foreground mt-1">
                {filter === 'active'
                  ? 'When the gateway or a detector refuses or flags something, it appears here within a minute. Check the Decisions page for what was decided, or the Rules page for what is switched on.'
                  : 'Switch to "Open and in progress" to see what needs action.'}
              </div>
            </div>
          ) : (
            <ul
              ref={listRef}
              role="listbox"
              aria-label="Incidents, most serious first. Arrow keys or j and k to move, Enter to open."
              aria-activedescendant={incidents[cursor] ? `incident-${incidents[cursor]!.id}` : undefined}
              tabIndex={0}
              onKeyDown={onListKey}
              className="max-h-[70vh] overflow-y-auto outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid="incident-list"
            >
              {incidents.map((i, index) => (
                <li
                  key={i.id}
                  id={`incident-${i.id}`}
                  role="option"
                  aria-selected={selected === i.id}
                  data-index={index}
                  onClick={() => { setCursor(index); setSelected(i.id); }}
                  className={`px-3 py-2 border-b border-border/40 cursor-pointer text-xs ${index === cursor ? 'bg-primary/10' : ''} ${selected === i.id ? 'border-l-2 border-l-primary' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <Badge variant={SEVERITY_VARIANT[i.severity]} className="capitalize">{i.severity}</Badge>
                    <span className="font-medium truncate">{i.worstDecision} on {i.storeKey || 'an unnamed store'}</span>
                    <span className="ml-auto text-muted-foreground">{STATUS_TEXT[i.status]}</span>
                  </div>
                  <div className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                    <span>by {i.actorId}</span>
                    <span>{i.decisionCount} decision{i.decisionCount === 1 ? '' : 's'}{i.suppressedCount > 0 ? `, ${i.suppressedCount} suppressed` : ''}</span>
                    <span>last {when(i.lastSeenAt)}</span>
                    {i.assignedTo && <span>assigned</span>}
                  </div>
                  {i.breach && <div className="mt-1"><ClockChips clocks={i.breach.clocks} /></div>}
                </li>
              ))}
            </ul>
          )}
        </GlassCard>

        <GlassCard className="p-4" data-testid="incident-detail">
          {!selected || !inc || !detail ? (
            <div className="text-sm text-muted-foreground">
              Pick an incident on the left (or press Enter on the highlighted one) to see what happened and act on it.
            </div>
          ) : (
            <div className="space-y-4">
              <CardHeader className="p-0">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Badge variant={SEVERITY_VARIANT[inc.severity]} className="capitalize">{inc.severity}</Badge>
                  {inc.worstDecision} on {inc.storeKey || 'an unnamed store'}
                </CardTitle>
                <CardDescription>
                  {inc.actorId} made {inc.decisionCount} decision{inc.decisionCount === 1 ? '' : 's'} from {when(inc.firstSeenAt)} to {when(inc.lastSeenAt)}. The store {SENSITIVITY_TEXT[inc.storeSensitivity]}. Status: {STATUS_TEXT[inc.status]}
                  {inc.outcome ? ` (${OUTCOME_TEXT[inc.outcome]}).` : '.'}
                </CardDescription>
              </CardHeader>

              {inc.breach && (
                <div className="border border-sev-high/40 rounded-md p-2 text-xs space-y-1">
                  <div className="font-medium flex items-center gap-1"><Flame className="h-3.5 w-3.5" /> Confirmed as breach {when(inc.breach.confirmedAt)}. Regulator clocks:</div>
                  <ClockChips clocks={inc.breach.clocks} />
                  <div className="text-muted-foreground">
                    {inc.breach.corePushStatus === 'not_sent' ? 'Not yet sent to FOCTTA Core. The record is in Compliance, Breaches.' : `FOCTTA Core: ${inc.breach.corePushStatus}`}
                  </div>
                  {canApprove && inc.breach.clocks.filter((c) => !c.doneAt).map((c) => (
                    <Button key={c.key} size="sm" variant="glass" disabled={busy !== null} onClick={() => {
                      const note = window.prompt(`What was filed for "${c.label}" (reference, channel)?`);
                      if (note && note.trim().length >= 3) void act(`clock-${c.key}`, () => markBreachClockDone(inc.breach!.id, c.key, note.trim()), `${c.label} recorded as filed.`);
                    }}>
                      Record {c.label} filed
                    </Button>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2" aria-label="Actions">
                {canAck && !closed && me && (
                  inc.assignedTo === me.actorId ? (
                    <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => void act('assign', () => assignIncident(inc.id, null), 'Unassigned.')}>Unassign me</Button>
                  ) : (
                    <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => void act('assign', () => assignIncident(inc.id, me.actorId), 'Assigned to you.')}><User className="h-3.5 w-3.5 mr-1" />Assign to me</Button>
                  )
                )}
                {canAck && !closed && !inc.acknowledgedAt && (
                  <Button size="sm" disabled={busy !== null} onClick={() => void act('ack', () => acknowledgeIncident(inc.id), 'Acknowledged.')}>Acknowledge</Button>
                )}
                {canAck && !closed && <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => setDialog('suppress')}>Suppress</Button>}
                {canRun && !closed && <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => setDialog('playbook')}>Run playbook</Button>}
                {canAck && !closed && <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => setDialog('breakglass')}>Start break-glass</Button>}
                {canAck && !inc.breach && (!closed || inc.outcome === 'true_positive') && (
                  <Button size="sm" variant="destructive" disabled={busy !== null} onClick={() => setDialog('breach')}>Confirm as breach</Button>
                )}
                {canAck && !closed && <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => setDialog('close')}>Close</Button>}
                {!canAck && !canRun && <span className="text-xs text-muted-foreground">You can read this incident. Acting on it needs the Security Operator role.</span>}
              </div>

              <div className="text-xs space-y-1">
                <div><span className="text-muted-foreground">Assigned to:</span> {inc.assignedTo ? `${inc.assignedTo === me?.actorId ? 'you' : inc.assignedTo}, by ${inc.assignedBy ?? '-'} at ${when(inc.assignedAt)}` : 'nobody yet'}</div>
                <div><span className="text-muted-foreground">Acknowledged:</span> {inc.acknowledgedAt ? `by ${inc.acknowledgedBy} at ${when(inc.acknowledgedAt)}` : 'not yet'}</div>
                {inc.closedAt && <div><span className="text-muted-foreground">Closed:</span> by {inc.closedBy} at {when(inc.closedAt)}: {inc.outcomeNote}</div>}
                <div className="text-muted-foreground">
                  Learning: this rule and actor were closed as false positives {detail.learning.falsePositivesForRuleAndActor} time(s) before; this store has {detail.learning.truePositivesOnStore} true positive(s).
                </div>
              </div>

              {(detail.playbookRuns.length > 0) && (
                <section>
                  <h3 className="text-xs font-semibold mb-1">Playbooks</h3>
                  {detail.playbookRuns.map((r) => (
                    <div key={r.id} className="border border-border/50 rounded p-2 mb-2 text-xs" data-testid="playbook-run">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{r.playbookName}</span>
                        <span className="text-muted-foreground">asked by {r.requestedBy} at {when(r.createdAt)}</span>
                        <span className="ml-auto">{r.status === 'pending_approval' ? 'Waiting for a second approver' : r.status === 'awaiting_manual' ? 'Manual steps open' : r.status}</span>
                      </div>
                      {r.status === 'pending_approval' && (
                        <div className="mt-1 flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5" /> Approve by {when(r.approvalExpiresAt)}. A different person who can approve rules must approve it.
                          {canApprove && (
                            <Button size="sm" disabled={busy !== null} onClick={() => void act(`approve-${r.id}`, () => approvePlaybookRun(r.id), 'Approved; the playbook ran.')}>Approve and run</Button>
                          )}
                        </div>
                      )}
                      {r.approver2 && <div className="text-muted-foreground">Approved by {r.approver2} at {when(r.approvedAt)}</div>}
                      <ol className="mt-1 list-decimal ml-4 space-y-0.5">
                        {r.stepsCompleted.map((s, index) => (
                          <li key={index}>
                            <span className={s.status === 'done' ? 'text-success' : s.status === 'failed' ? 'text-sev-high' : ''}>
                              {s.step.type}: {s.status === 'done' ? 'done' : s.status === 'failed' ? 'could not act' : s.manual?.completedAt ? `done by hand by ${s.manual.completedBy} at ${when(s.manual.completedAt)}` : 'manual step'}
                            </span>
                            <div className="text-muted-foreground">{s.status === 'manual' ? (s.manual?.completedAt ? s.manual.note : s.manual?.instruction) : s.message}</div>
                            {s.status === 'manual' && !s.manual?.completedAt && canRun && (
                              <Button size="sm" variant="glass" disabled={busy !== null} onClick={() => {
                                const note = window.prompt('What did you do, and where is the evidence?');
                                if (note && note.trim().length >= 3) void act(`step-${r.id}-${index}`, () => completeManualStep(r.id, index, note.trim()), 'Step recorded.');
                              }}>Record as done</Button>
                            )}
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                  {pendingRuns.length > 0 && !canApprove && <div className="text-xs text-muted-foreground">The tenant's DPO and legal reviewer users, if there are any, have been notified; someone holding rule approval must approve.</div>}
                </section>
              )}

              <section>
                <h3 className="text-xs font-semibold mb-1">What happened, who did it and when</h3>
                <ol className="text-xs space-y-0.5" data-testid="incident-timeline">
                  {detail.actions.length === 0 && <li className="text-muted-foreground">Nobody has acted on it yet.</li>}
                  {detail.actions.map((a) => (
                    <li key={a.id}>{when(a.at)}: {ACTION_TEXT[a.action] ?? a.action} by {a.actorId === me?.actorId ? 'you' : a.actorId}</li>
                  ))}
                </ol>
              </section>

              <section>
                <h3 className="text-xs font-semibold mb-1">Decisions</h3>
                <ul className="text-xs space-y-0.5">
                  {detail.decisions.map((d) => (
                    <li key={d.id}>
                      <code className="font-mono">{d.requestId ?? d.id}</code> {d.decision} at {when(d.decidedAt)}{d.suppressionId ? ' (suppressed)' : ''}
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          )}
        </GlassCard>
      </div>

      {inc && dialog === 'suppress' && <SuppressDialog incident={inc} allUnits={me?.businessUnits.all !== false} busy={busy !== null} onClose={() => setDialog(null)} onSubmit={(scope, reason, hours) => act('suppress', () => suppressIncident(inc.id, { scope, reason, expiresInHours: hours }), `Suppressed: ${SCOPE_TEXT[scope].toLowerCase()}, for ${DURATIONS.find((x) => x.hours === hours)?.label ?? `${hours} hours`}. Later hits are still counted.`).then((ok) => ok && setDialog(null))} />}
      {inc && dialog === 'playbook' && <PlaybookDialog playbooks={playbooks} principalCount={detail?.playbookPrincipalCount ?? 0} busy={busy !== null} onClose={() => setDialog(null)} onSubmit={(name) => act('playbook', () => runIncidentPlaybook(inc.id, name), 'Playbook started. A two-person playbook waits for a second approver.').then((ok) => ok && setDialog(null))} />}
      {inc && dialog === 'breakglass' && <BreakGlassDialog incident={inc} tables={[...new Set((detail?.decisions ?? []).flatMap((d) => d.tables))].slice(0, 20)} busy={busy !== null} onClose={() => setDialog(null)} onSubmit={(input) => act('breakglass', () => startIncidentBreakGlass(inc.id, input), 'Break-glass requested. It lifts nothing until a different person approves it on the Break-glass page.').then((ok) => ok && setDialog(null))} />}
      {inc && dialog === 'breach' && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm as breach?</DialogTitle>
              <DialogDescription>
                This creates the breach record, filled in from this incident (stores, data categories, how many people), and starts the regulator clocks now: DPDP Board intimation, the detailed report at 72 hours, CERT-In at 6 hours, and RBI or SEBI at 6 hours if your regulator set names them. It cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="glass" onClick={() => setDialog(null)}>Cancel</Button>
              <Button variant="destructive" disabled={busy !== null} onClick={() => void act('breach', () => confirmIncidentBreach(inc.id), 'Confirmed as breach. The clocks are running; the record is on the Compliance Breaches page.').then((ok) => ok && setDialog(null))}>
                <ShieldAlert className="h-4 w-4 mr-1" /> Confirm as breach
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {inc && dialog === 'close' && (
        <CloseDialog breach={!!inc.breach} busy={busy !== null} onClose={() => setDialog(null)} onSubmit={async (outcome, note) => {
          setBusy('close');
          setNotice(null);
          try {
            const r = await closeIncident(inc.id, outcome, note);
            setNotice({ kind: 'ok', text: `Closed as ${OUTCOME_TEXT[outcome].toLowerCase()}.${outcome === 'false_positive' ? ` This rule and actor have now been closed as false positives ${r.learning.falsePositivesForRuleAndActor} time(s).` : ''}` });
            setOffer(r.offerSuppression ?? null);
            setDialog(null);
            await load();
            await loadDetail(inc.id);
          } catch (err) {
            setNotice({ kind: 'refused', text: refusalText(err) });
          } finally {
            setBusy(null);
          }
        }} />
      )}
    </PageContainer>
  );
}

function SuppressDialog(props: { incident: Incident; allUnits: boolean; busy: boolean; onClose: () => void; onSubmit: (scope: SuppressionScope, reason: string, hours: number) => void }) {
  // Signatures the controller refuses: a trigger (every gateway alert) or an engine whose refusals must stay visible.
  const triggerOnly = props.incident.ruleKey.startsWith('trigger:') || GUARD_SIGNATURES.has(props.incident.ruleKey);
  // Decisions with no actor group under "unknown", which stands for everyone: never suppressed by actor.
  const noActor = props.incident.actorId === 'unknown';
  const [scope, setScope] = useState<SuppressionScope>(noActor ? 'signature' : 'actor_store');
  const nothingOffered = noActor && (triggerOnly || !props.allUnits);
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState(24);
  return (
    <Dialog open onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Suppress</DialogTitle>
          <DialogDescription>
            Stops new incidents, their notifications and the gateway's SIEM event for what you choose, until it ends. Not suppressed: other SIEM feeds (database logs, API abuse) and the notification the rule evaluation route raises. Every hit is still counted and shown under "With suppressed hits".
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-1 text-sm">
          <legend className="text-xs text-muted-foreground mb-1">What to suppress</legend>
          {(Object.keys(SCOPE_TEXT) as SuppressionScope[]).map((s) => (
            <label key={s} className="flex items-center gap-2">
              <input type="radio" name="scope" checked={scope === s} disabled={(!props.allUnits && s !== 'actor_store') || (s === 'signature' && triggerOnly) || (s !== 'signature' && noActor)} onChange={() => setScope(s)} />
              {SCOPE_TEXT[s]}
              {s === 'actor_store' && ` (${props.incident.actorId} on ${props.incident.storeKey || 'this store'})`}
              {s === 'actor' && ` (${props.incident.actorId})`}
              {s === 'signature' && ` (${props.incident.ruleKey})`}
            </label>
          ))}
          {noActor && <div className="text-xs text-muted-foreground">These decisions name no actor (database-log reads, rule evaluation), so "this actor" would mean everyone. Not offered.</div>}
          {triggerOnly && props.allUnits && <div className="text-xs text-muted-foreground">{props.incident.ruleKey.startsWith('trigger:') ? 'No named rule or engine raised this incident, so a signature suppression would hide every gateway alert.' : 'This engine refuses on grounds that must stay visible (children\'s data, restriction, honeytoken), so its refusals are not suppressed by signature.'} Not offered.</div>}
          {!props.allUnits && <div className="text-xs text-muted-foreground">Your access is limited to some business units, so you can suppress only this actor on this store.</div>}
        </fieldset>
        <label className="text-sm block">
          Why (required)
          <textarea className="w-full mt-1 rounded border bg-transparent p-2 text-sm" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        <label className="text-sm block">
          For
          <select className="ml-2 rounded border bg-transparent p-1" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {DURATIONS.map((d) => <option key={d.hours} value={d.hours}>{d.label}</option>)}
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="glass" onClick={props.onClose}>Cancel</Button>
          <Button disabled={props.busy || nothingOffered || reason.trim().length < 3} onClick={() => props.onSubmit(scope, reason.trim(), hours)}>Suppress</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** What a playbook's session and block steps do, said before it runs (review 1 at df9f7823). */
export function containmentEffects(p: DetectionPlaybook | undefined, principalCount: number): string[] {
  const types = new Set((p?.steps ?? []).map((s) => (s as { type?: string }).type ?? ''));
  const who = principalCount > 0 ? `the ${principalCount === 50 ? 'up to 50' : principalCount} data principal(s) whose records this incident's statements read` : 'no data principal (none was recorded, so these steps become manual)';
  const out: string[] = [];
  if (types.has('quarantine_session') || types.has('force_session_logout') || types.has('block_access')) {
    out.push(`Closes every gateway session, of any actor and including application connections, that touched the records of ${who}.`);
  }
  if (types.has('block_access')) {
    out.push(`Refuses everyone's reads of the records of ${who} for 24 hours (a processing restriction; break-glass cannot lift it).`);
  }
  if (out.length > 0) out.push("It does not block the actor's own access.");
  return out;
}

function PlaybookDialog(props: { playbooks: DetectionPlaybook[]; principalCount: number; busy: boolean; onClose: () => void; onSubmit: (name: string) => void }) {
  const [name, setName] = useState(props.playbooks[0]?.name ?? '');
  const [understood, setUnderstood] = useState(false);
  const chosen = props.playbooks.find((p) => p.name === name);
  const effects = containmentEffects(chosen, props.principalCount);
  return (
    <Dialog open onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run a playbook on this incident</DialogTitle>
          <DialogDescription>
            Steps act only where FOCTTA can act (close gateway sessions and restrict the records of the data principals this incident read, write to the audit trail, notify the DPO); every other step becomes a checklist item for a person.
          </DialogDescription>
        </DialogHeader>
        {props.playbooks.length === 0 ? (
          <div className="text-sm text-muted-foreground">No playbooks could be loaded. Open the Playbooks page to check.</div>
        ) : (
          <select className="w-full rounded border bg-transparent p-2 text-sm" value={name} onChange={(e) => { setName(e.target.value); setUnderstood(false); }}>
            {props.playbooks.map((p) => <option key={p.name} value={p.name}>{p.name}{p.requiresTwoPersonRule ? ' (two people)' : ''}</option>)}
          </select>
        )}
        {chosen?.requiresTwoPersonRule && <div className="text-xs text-muted-foreground">Needs a second, different person who can approve rules, within 30 minutes. Nothing runs before that.</div>}
        {effects.length > 0 && (
          <div className="border border-warning/50 rounded p-2 text-xs space-y-1" data-testid="playbook-effects">
            <div className="font-medium">What this playbook will do</div>
            {effects.map((e) => <div key={e}>{e}</div>)}
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} /> I understand what it will do
            </label>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="glass" onClick={props.onClose}>Cancel</Button>
          <Button disabled={props.busy || !name || (effects.length > 0 && !understood)} onClick={() => props.onSubmit(name)}>{chosen?.requiresTwoPersonRule ? 'Ask for approval' : 'Run'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BreakGlassDialog(props: { incident: Incident; tables: string[]; busy: boolean; onClose: () => void; onSubmit: (i: { principalActorId: string; scopeTables: string[]; justification: string; expiresInMinutes: number }) => void }) {
  // The no-actor placeholder is not a principal: never prefilled (review at abfa9c06).
  const [principal, setPrincipal] = useState(props.incident.actorId === 'unknown' ? '' : props.incident.actorId);
  // The tables exactly as the gateway reported them: the scope is matched on the whole name, schema included.
  const [tables, setTables] = useState(props.tables.length > 0 ? props.tables.join(', ') : props.incident.storeKey);
  const [minutes, setMinutes] = useState(60);
  const [justification, setJustification] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start break-glass</DialogTitle>
          <DialogDescription>
            Once a different person approves it, the gateway lets this principal through a refusal on these tables until it ends. Nothing is lifted on a statement that touches a table holding children's data (DPDPA Section 9), whatever refused it, and never a restriction or erasure. A follow-up summary is due afterwards.
          </DialogDescription>
        </DialogHeader>
        <label className="text-sm block">Principal (the database login the gateway reports)
          <input className="w-full mt-1 rounded border bg-transparent p-2 text-sm" value={principal} onChange={(e) => setPrincipal(e.target.value)} />
        </label>
        <label className="text-sm block">Tables, comma separated, as the gateway names them (schema included). A session on public.customers does not cover a statement the gateway reports as customers. If your access is limited to some units, every table must be schema-qualified and belong to your units. Empty means every table; a statement naming no table is never let through
          <input className="w-full mt-1 rounded border bg-transparent p-2 text-sm" value={tables} onChange={(e) => setTables(e.target.value)} />
        </label>
        <label className="text-sm block">For
          <select className="ml-2 rounded border bg-transparent p-1" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
            {[15, 30, 60, 120, 240].map((m) => <option key={m} value={m}>{m} minutes</option>)}
          </select>
        </label>
        <label className="text-sm block">Why (at least 20 characters)
          <textarea className="w-full mt-1 rounded border bg-transparent p-2 text-sm" rows={3} value={justification} onChange={(e) => setJustification(e.target.value)} />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="glass" onClick={props.onClose}>Cancel</Button>
          <Button
            disabled={props.busy || principal.trim().length === 0 || justification.trim().length < 20}
            onClick={() => props.onSubmit({ principalActorId: principal.trim(), scopeTables: tables.split(',').map((t) => t.trim()).filter(Boolean), justification: justification.trim(), expiresInMinutes: minutes })}
          >
            Ask for approval
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CloseDialog(props: { breach: boolean; busy: boolean; onClose: () => void; onSubmit: (o: IncidentOutcome, note: string) => void }) {
  const [outcome, setOutcome] = useState<IncidentOutcome>(props.breach ? 'true_positive' : 'false_positive');
  const [note, setNote] = useState('');
  return (
    <Dialog open onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close the incident</DialogTitle>
          <DialogDescription>
            The outcome is recorded against the rule and the actor. A false positive offers a suppression; a true positive counts against the store.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-1 text-sm">
          {(Object.keys(OUTCOME_TEXT) as IncidentOutcome[]).map((o) => (
            <label key={o} className="flex items-center gap-2">
              <input type="radio" name="outcome" checked={outcome === o} disabled={props.breach && o !== 'true_positive'} onChange={() => setOutcome(o)} />
              {OUTCOME_TEXT[o]}
            </label>
          ))}
          {props.breach && <div className="text-xs text-muted-foreground">A confirmed breach closes only as a true positive.</div>}
        </fieldset>
        <label className="text-sm block">Note (required)
          <textarea className="w-full mt-1 rounded border bg-transparent p-2 text-sm" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="glass" onClick={props.onClose}>Cancel</Button>
          <Button disabled={props.busy || note.trim().length < 3} onClick={() => props.onSubmit(outcome, note.trim())}>Close incident</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
